/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useEffect, useMemo, useState } from 'react';
import { withTheme } from '@emotion/react';
import { useDispatch, useSelector } from 'react-redux';
import { selectLoader } from 'modules/llm/selectors';
import { toast } from 'react-toastify';
import { subDays } from 'date-fns';
import ERROR_TYPES from 'modules/common/constants/error-types';
import TOAST_TYPES from 'modules/common/constants/toast-types';
import { selectOrganizationId } from 'modules/common/auth/selectors';
import { selectNotification } from 'modules/common/notifications/selectors';
import { llmActions } from 'modules/llm/slice';
import PAGINATION from 'modules/common/constants/pagination-limits';
import { Loader } from 'modules/common/components';
import { CardContent, Grid, Card, Typography, Stack } from '@mui/material';
import { Button } from './style';
import LlmActivityTable from '../llm-activity-table';
import LlmModelUsageTable from '../llm-model-usage-table';
import DashboardMetrics from '../overview';
import UsageByModelChart from '../usage-charts';
import AddModelForm from '../add-model';
import UpdateModelForm from '../update-model';

const LlmView = ({ theme }) => {
  //
  const dispatch = useDispatch();
  const loading = useSelector(selectLoader);
  const notification = useSelector(selectNotification);
  const organizationId = useSelector(selectOrganizationId);
  //
  const [period, setPeriod] = useState('30');
  const [open, setOpen] = useState(false);
  const [openInvite, setOpenInvite] = useState(false);

  const { from, to } = useMemo(
    () => ({
      from: subDays(new Date(), Number(period)).toISOString(),
      to: new Date().toISOString(),
    }),
    [period]
  );

  const logsQuery = (page) =>
    `from=${from}&to=${to}&limit=${PAGINATION.LLM_LOGS_PER_PAGE_COUNT}&page=${page}&sortBy=createdAt:desc`;

  useEffect(() => {
    if (!organizationId) return;
    dispatch(
      llmActions.getDefaultModelsInformation({
        query: 'limit=5&key=llmDefaultModels',
      })
    );
    dispatch(
      llmActions.getAllModels({
        organizationId,
        query: `limit=${PAGINATION.USERS_PER_PAGE_COUNT}`,
      })
    );
    dispatch(
      llmActions.getModelUsage({
        organizationId,
        query: `from=${from}&to=${to}`,
      })
    );
    dispatch(
      llmActions.getLlmLogs({
        organizationId,
        query: logsQuery(1),
      })
    );
    dispatch(
      llmActions.getChartLogs({
        organizationId,
        query: `from=${from}&to=${to}&limit=${PAGINATION.LLM_LOGS_PER_PAGE_COUNT}&sortBy=createdAt:asc`,
      })
    );
  }, [organizationId, from, to]);

  useEffect(() => {
    if (notification?.isEnabled && notification?.type === ERROR_TYPES.SUCCESS) {
      toast(notification?.message, { type: TOAST_TYPES.SUCCESS });
    }
  }, [notification]);
  //
  return (
    <Loader loading={loading}>
      <Card>
        <CardContent sx={{ backgroundColor: theme.palette.background.default }}>
          <Grid container direction="row" justifyContent="space-between" pb={4}>
            <Stack direction="column">
              <Typography variant="h2">LLM Usage</Typography>
              <Typography variant="caption">Your usage across the models on LLM features</Typography>
            </Stack>

            <Grid item>
              <Button
                sx={{ marginRight: 2 }}
                variant="contained"
                color="primary"
                onClick={() => setOpenInvite(true)}
              >
                Add Model
              </Button>
              <Button variant="contained" color="primary" onClick={() => setOpen(true)}>
                Update Model
              </Button>
            </Grid>
          </Grid>
          <DashboardMetrics period={period} onPeriodChange={setPeriod} />
          <UsageByModelChart />
          <Grid container direction="row" spacing={2} justifyContent="space-between" my={4}>
            <Grid item sx={{ flexGrow: 1, flexBasis: 1 }}>
              <LlmModelUsageTable />
            </Grid>
          </Grid>
          <Grid container direction="row" spacing={2} justifyContent="space-between">
            <Grid item sx={{ flexGrow: 1, flexBasis: 1 }}>
              <LlmActivityTable
                onPageChange={(page) =>
                  dispatch(llmActions.getLlmLogs({ organizationId, query: logsQuery(page + 1) }))
                }
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      <UpdateModelForm open={open} onClose={() => setOpen(false)} />
      <AddModelForm open={openInvite} onClose={() => setOpenInvite(false)} />
    </Loader>
  );
};
//
export default withTheme(LlmView);
